import { LoadingState } from '../../../../../components/blackrock/advisor-center/empty-state/EmptyStateProps.ts';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { SeriesItemData } from '../../../../../components/blackrock/advisor-center/cards/series-item-card/SeriesItemCardProps.ts';

/**
 * Custom hook to load SeriesItemData.
 * @param {string | undefined | null} ajaxUrl - Ajax url to fetch seriesItem list. It is optional. If it is undefined loading doesn't happen.
 * @param {SeriesItemData[]} items - The list of series items. In case if it isn't an empty list we don't show loading state or error state.
 * @param {Dispatch<SetStateAction<SeriesItemData[]>>} setItems - Function to set the loaded series items.
 * @returns {LoadingState} loadingState - The loading state.
 * @returns {() => void} retry - Function to retry, reload items.
 */
export const useLoad = (
	ajaxUrl: string | undefined | null,
	items: SeriesItemData[],
	setItems: Dispatch<SetStateAction<SeriesItemData[]>>
): [loadingState: LoadingState, retry: () => void] => {
	if (!ajaxUrl) {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		return [LoadingState.LOADED, undefined];
	} else {
		const [loadingState, setLoadingState] = useState<LoadingState>(
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			items?.length > 0 ? LoadingState.LOADED : null
		);

		// request series items
		useEffect(() => {
			const requestController = load();
			return () => requestController.abort();
		}, [ajaxUrl]);

		// retry (after a fetch error)
		const retry = () => load();

		const load = (): AbortController => {
			if (!items?.length) {
				setLoadingState(LoadingState.LOADING);
			}

			const requestController = new AbortController();
			const signal = requestController.signal;

			fetch(ajaxUrl, { signal })
				.then((response) => {
					if (response?.status !== 200) {
						throw new Error(`Network response status: ${response?.status}`);
					}
					return response.json();
				})
				.then((response: SeriesItemData[]) => {
					setLoadingState(LoadingState.LOADED);
					setItems(response);
				})
				.catch((e: Error) => {
					if (!items?.length) {
						setLoadingState(LoadingState.ERROR);
					}
					console.error(e);
				});

			return requestController;
		};

		return [loadingState, retry];
	}
};
